'use client';

import { useEffect } from 'react';

// Drift below this many px counts as the target still being aligned
const DRIFT_TOLERANCE_PX = 1;
// How long to keep re-aligning a hash target while late-loading content settles
const SETTLE_DURATION_MS = 1500;

/**
 * Handles same-page hash-anchor navigation:
 * - forces a scroll on every hash click, even when the hash is unchanged
 * - moves focus to the target so keyboard and screen-reader users follow the scroll
 * - re-aligns the target while late-loading content above it shifts the page down
 *
 * Deliberately does not preload images. Re-aligning after the fact costs no extra
 * requests and also covers fonts, iframes and late-hydrating components.
 */
export function useHashLinkScroll() {
  useEffect(() => {
    let stopSettling: (() => void) | null = null;

    // Document-relative offset of an element, unaffected by an in-flight smooth scroll
    const getDocumentTop = (el: Element) => el.getBoundingClientRect().top + window.scrollY;

    // Make the target focusable if it isn't already, then focus it without fighting the scroll
    const focusTarget = (target: HTMLElement) => {
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      target.focus({ preventScroll: true });
    };

    // Re-scroll to the target whenever content above it shifts, until layout settles or the user takes over
    const keepTargetAligned = (target: HTMLElement) => {
      stopSettling?.();

      let lastTop = getDocumentTop(target);
      let frame = 0;
      const deadline = performance.now() + SETTLE_DURATION_MS;

      const stop = () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('wheel', stop);
        window.removeEventListener('touchstart', stop);
        window.removeEventListener('keydown', stop);
        stopSettling = null;
      };

      const tick = () => {
        if (performance.now() > deadline) {
          stop();
          return;
        }
        const top = getDocumentTop(target);
        if (Math.abs(top - lastTop) > DRIFT_TOLERANCE_PX) {
          lastTop = top;
          target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
        frame = requestAnimationFrame(tick);
      };

      // Hand control back the moment the user scrolls or types
      window.addEventListener('wheel', stop, { passive: true, once: true });
      window.addEventListener('touchstart', stop, { passive: true, once: true });
      window.addEventListener('keydown', stop, { once: true });

      stopSettling = stop;
      frame = requestAnimationFrame(tick);
    };

    // Force a scroll on every same-page hash click, even when the hash is unchanged
    const handleClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;

      const anchor = (e.target as Element | null)?.closest?.('a');
      if (!anchor) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const url = new URL(href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname ||
        !url.hash ||
        url.hash === '#'
      )
        return;

      const id = decodeURIComponent(url.hash.slice(1));
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      if (window.location.hash !== url.hash) {
        history.pushState(null, '', url.hash);
      }
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      focusTarget(target);
      keepTargetAligned(target);
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      stopSettling?.();
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
}
